import c from 'classnames';
import { useLinks } from 'core/components/linksContext';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonthYearDot } from 'core/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'site/components/auth/AuthProvider';
import { SubscriptionPublicTypeEnum } from 'site/graphql';
import SubscribeButton from '../../../subscription/SubscribeButton';
import UnsubscribeButton from './UnsubscribeButton';

type IBaseSubscriptionProps = {} & WithStyles<typeof styles>;

const BaseSubscription: React.FC<IBaseSubscriptionProps> = ({ classes }) => {
    const {
        myAccount: {
            subscription,
            accountSubscription: { subscriptionAutopayment },
        },
    } = useAuth();
    const links = useLinks();
    const activeSubscriptionType = subscription.type;

    let subscribeButtonMonthLabel = '200 р./месяц';
    let subscribeButtonYearLabel = '100 р./месяц, 1200 р./год';

    if (activeSubscriptionType) {
        if (activeSubscriptionType === SubscriptionPublicTypeEnum.Month) {
            subscribeButtonMonthLabel = 'Продлить (200 р./месяц)';
            subscribeButtonYearLabel = 'Оформить на год (100 р./месяц)';
        } else {
            subscribeButtonMonthLabel = 'Оформить на месяц (200 р.)';
            subscribeButtonYearLabel = 'Продлить (1200 р./год)';
        }
    }

    return (
        <div className={classes.root}>
            {activeSubscriptionType && (
                <p className={c(classes.active, classes.mb1rem)}>
                    {activeSubscriptionType === SubscriptionPublicTypeEnum.Month ? 'Месячная' : 'Годовая'} подписка
                    оформлена до {formatToLocalDayMonthYearDot(subscription.until)}
                </p>
            )}
            <p>Условия оформления подписки:</p>
            <p className={c(classes.mb1rem, classes.redText)}>Действует супер предложение: скидка 50% на сервисы.</p>
            <p>
                1 месяц — <span style={{ textDecoration: 'line-through' }}>400</span>{' '}
                <span className={classes.redText}>200 рублей</span>
            </p>
            <p className={classes.mb1rem}>
                1 год — <span style={{ textDecoration: 'line-through' }}>2400</span>{' '}
                <span className={classes.redText}>1200 рублей</span>
            </p>
            <p>Подписка предоставляет возможность пользоваться сервисами:</p>
            <ul className={classes.mb1rem}>
                <li>
                    <Link to={links.highLow.url()}>Минимумы и максимумы акций за год</Link>
                </li>
                <li>
                    <Link to={links.dividendTable.url()}>Таблица дивидендных акций</Link>
                </li>
                <li>
                    <Link to={links.ofz.url()}>Собрать портфель из облигаций</Link>
                </li>
                <li>Совершать сделки напрямую с сайта</li>
            </ul>
            <p className={classes.mb1rem}>
                В момент первой оплаты подписки вам будет подключен автоплатеж (списание суммы, равной стоимости
                подписки, будет производиться 1 раз, в выбранный период, с привязанной карты).
            </p>
            <p className={classes.mb1rem}>
                Отменить подписку можно в любое время. После отмены подписки автоплатежи проходить не будут.
            </p>
            <div className={classes.buttonsWrapper}>
                <div
                    className={c(classes.subscribeButtonsWrapper, {
                        [classes.subscribeButtonsWrapperAutoPayment]: subscriptionAutopayment,
                    })}
                >
                    <SubscribeButton
                        className={classes.subscribeButton}
                        label={subscribeButtonMonthLabel}
                        type={SubscriptionPublicTypeEnum.Month}
                    />
                    <SubscribeButton
                        className={classes.subscribeButton}
                        label={subscribeButtonYearLabel}
                        type={SubscriptionPublicTypeEnum.Year}
                    />
                </div>
                {subscriptionAutopayment && (
                    <UnsubscribeButton
                        className={c(classes.unsubscribeButton, {
                            [classes.unsubscribeButtonYear]: activeSubscriptionType === SubscriptionPublicTypeEnum.Year,
                        })}
                    />
                )}
            </div>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        root: {
            padding: '1rem 1.5rem',
            border: '1px solid rgba(0, 0, 0, 0.2)',
            fontSize: 16,
            lineHeight: 18 + 'px',

            '& p:not($mb1rem)': {
                margin: 0,
            },
        },
        mb1rem: {
            marginBottom: 1 + 'rem',
        },
        active: {
            color: theme.colors.accent,
            fontWeight: 'bold',
        },
        buttonsWrapper: {
            display: 'flex',
        },
        subscribeButtonsWrapper: {
            display: 'flex',
            justifyContent: 'space-between',
            flexGrow: 1,
        },
        subscribeButtonsWrapperAutoPayment: {
            flexDirection: 'column',
            marginRight: 1 + 'rem',

            '& $subscribeButton:first-child': {
                marginBottom: 1 + 'rem',
                marginRight: 0,
            },
        },
        subscribeButton: {
            fontSize: 14,
            fontWeight: 'bold',

            '&:first-child': {
                marginRight: 1 + 'rem',
            },
        },
        unsubscribeButton: {
            alignSelf: 'flex-start',
        },
        unsubscribeButtonYear: {
            alignSelf: 'flex-end',
        },
        redText: {
            color: theme.colors.accent,
        },
    });

export default injectSheet(styles)(BaseSubscription);
